import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaChevronRight, FaHeart, FaBriefcase, FaUsers, FaStar, FaMusic } from 'react-icons/fa';
import PageBanner from '../components/PageBanner';
import CategoryImageGallery from '../components/CategoryImageGallery';
import Breadcrumbs from '../components/Breadcrumbs';
import JsonLd from '../components/JsonLd';
import { buildBreadcrumbListJsonLd } from '../utils/breadcrumbSchema';
import { eventsMenu } from '../constants/eventsData';
import { getCategoryAssets } from '../utils/categoryAssets';
import './Events.css';

const categoryIcons = {
  wedding: FaHeart,
  corporate: FaBriefcase,
  social: FaUsers,
  other: FaStar,
  concert: FaMusic,
};

const Events = () => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const { bannerSrc, gallery } = useMemo(
    () => getCategoryAssets('eventsHub'),
    [],
  );

  const breadcrumbLd = buildBreadcrumbListJsonLd([
    { name: 'Home', path: '/' },
    { name: 'Our Events', path: '/events' },
  ]);

  return (
    <div className="events-hub-page">
      <JsonLd data={breadcrumbLd} />
      <Breadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: 'Our Events' },
        ]}
      />
      <PageBanner
        title="Our Events"
        subtitle="From weddings to corporate gatherings — we plan, design, and execute every celebration with excellence."
        image={bannerSrc}
        imageAlt="Wedding, corporate, and concert event planning — Panthi Events, Ahmedabad, Gujarat, India"
        imageWidth={1920}
        imageHeight={1080}
      />

      <section className="events-hub-section">
        <div className="events-hub-container">
          <CategoryImageGallery
            items={gallery}
            title="Celebrations in focus"
            className="events-hub-gallery"
          />

          <div className="events-hub-header">
            <span className="events-hub-badge">Events Menu</span>
            <h2 className="events-hub-title">The Art of Every Celebration</h2>
            <div className="title-underline"></div>
            <p className="events-hub-subtitle">
              Choose a category below to explore our event services and sub-events. Each celebration is crafted with precision and passion.
            </p>
          </div>

          <div className="events-category-cards">
            {eventsMenu.map((category) => {
              const Icon = categoryIcons[category.id] || FaStar;
              const isExpanded = expandedCategory === category.id;

              return (
                <div
                  key={category.id}
                  className={`events-category-card ${isExpanded ? 'expanded' : ''}`}
                  onMouseEnter={() => setExpandedCategory(category.id)}
                  onMouseLeave={() => setExpandedCategory(null)}
                >
                  <Link to={category.path} className="events-card-main-link">
                    <div className="events-card-icon-wrap">
                      <Icon className="events-card-icon" />
                    </div>
                    <h3 className="events-card-title">{category.title}</h3>
                    <span className="events-card-cta">
                      View details <FaChevronRight />
                    </span>
                  </Link>

                  <div className="events-card-submenu">
                    <button
                      type="button"
                      className="events-submenu-toggle"
                      onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                      aria-expanded={isExpanded}
                    >
                      <span>{isExpanded ? 'Hide' : 'Show'} sub-events</span>
                      <FaChevronDown className={`chevron ${isExpanded ? 'open' : ''}`} />
                    </button>
                    <ul className={`events-submenu-list ${isExpanded ? 'open' : ''}`}>
                      {category.subItems.map((item, index) => (
                        <li key={index}>
                          <span className="events-submenu-item">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to={category.path} className="events-card-button">
                    Explore {category.title}
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="events-hub-cta">
            <p>Not sure which event fits? We’re here to help.</p>
            <Link to="/contact" className="events-hub-cta-btn">Get in Touch</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
